/**
 * Who an advice document says the advice came from.
 *
 * A Statement of Advice has to name the entities standing behind it, and which entities those are
 * depends on HOW the firm's advice is provided — not on whether it is licensed. Three structures,
 * three different lines:
 *
 *   own_afsl      the practice IS the licensee. Its people are representatives of it by employment
 *                 and are NOT authorised representatives — ASIC does not put an employee on the AR
 *                 register — so the line must not call them one.
 *   corporate_ar  the practice is authorised by a licensee, and the adviser is authorised too. Three
 *                 entities: the person, the practice, the licensee.
 *   individual_ar the person is authorised, with no practice company between them and the licensee.
 *
 * Anything else — an accountant, a provider, a firm that has not answered yet — is not giving the
 * advice at all. We are, under our own licence, and the document says so plainly.
 *
 * Kept as a pure function with no rendering in it because it is the one piece of this feature a
 * client reads and a regulator could ask about, and because a string this consequential should be
 * testable without building a document around it.
 */

export interface AdviceProvider {
    /** The practice the client deals with. */
    firmName: string;
    /** The person who prepared the advice. */
    adviserName: string;
    /** Their ASIC representative number — the same number as their AR appointment where they have one. */
    adviserNumber?: string | null;
    licenseeName?: string | null;
    afslNumber?: string | null;
    adviceStructure?: 'own_afsl' | 'corporate_ar' | 'individual_ar' | null;
    carName?: string | null;
    carNumber?: string | null;
}

/** Our own details, for advice given under our licence rather than the firm's. */
export interface PlatformLicence {
    entityName: string;
    /** Absent until it is configured. Never invented — a made-up AFSL on a real document is worse
     *  than an incomplete one, because it looks finished. */
    afslNumber?: string | null;
}

const clean = (v: string | null | undefined): string => (v ?? '').trim();

export interface Attribution {
    /** The compliance line itself, ready to render. */
    line: string;
    /**
     * Whether every entity the structure requires was actually supplied. False means the document is
     * carrying an incomplete attribution and a person needs to finish the firm profile — the caller
     * decides how loudly to say so, but nothing should quietly present it as complete.
     */
    complete: boolean;
    /** Which entity is giving the advice — 'firm' under its own arrangements, or 'platform' under ours. */
    provider: 'firm' | 'platform';
}

export function adviceAttribution(p: AdviceProvider, platform: PlatformLicence): Attribution {
    const adviser = clean(p.adviserName) || 'Your adviser';
    const licensee = clean(p.licenseeName);
    const afsl = clean(p.afslNumber);
    const adviserNo = clean(p.adviserNumber);

    if (p.adviceStructure === 'own_afsl') {
        // "Representative", never "authorised representative": an employee or director of the
        // licensee is the former and not the latter, and the difference is a matter of fact.
        const parts = [adviser, `a representative of ${clean(p.firmName) || licensee || 'the licensee'}`];
        if (licensee && clean(p.firmName) && licensee !== clean(p.firmName)) parts.push(licensee);
        if (afsl) parts.push(`AFSL ${afsl}`);
        return { line: `${parts.join(', ')}.`, complete: Boolean(licensee || clean(p.firmName)) && Boolean(afsl), provider: 'firm' };
    }

    if (p.adviceStructure === 'corporate_ar') {
        const carName = clean(p.carName) || clean(p.firmName);
        const carNo = clean(p.carNumber);
        const parts = [adviser];
        if (adviserNo) parts.push(`Authorised Representative No. ${adviserNo}`);
        if (carName) parts.push(`of ${carName}`);
        if (carNo) parts.push(`Corporate Authorised Representative No. ${carNo}`);
        if (licensee) parts.push(`of ${licensee}`);
        if (afsl) parts.push(`AFSL ${afsl}`);
        return {
            line: `${parts.join(', ')}.`,
            complete: Boolean(adviserNo && carName && carNo && licensee && afsl),
            provider: 'firm',
        };
    }

    if (p.adviceStructure === 'individual_ar') {
        const parts = [adviser];
        if (adviserNo) parts.push(`Authorised Representative No. ${adviserNo}`);
        if (licensee) parts.push(`of ${licensee}`);
        if (afsl) parts.push(`AFSL ${afsl}`);
        return { line: `${parts.join(', ')}.`, complete: Boolean(adviserNo && licensee && afsl), provider: 'firm' };
    }

    // No structure recorded: the advice is ours, given under our licence. The firm is named as the
    // introducer and nothing more — the partner terms are explicit that a non-adviser professional
    // must not be held out as the provider of the advice.
    const platformName = clean(platform.entityName) || 'AgedCareWise Pty Ltd';
    const platformAfsl = clean(platform.afslNumber);
    const firm = clean(p.firmName);
    const head = platformAfsl ? `${platformName}, AFSL ${platformAfsl}` : platformName;
    return {
        line: firm ? `${head}. Referred by ${firm}, who is not the provider of this advice.` : `${head}.`,
        complete: Boolean(platformAfsl),
        provider: 'platform',
    };
}

/*
 * Who the signed-in firm is, for the advice documents.
 *
 * A module-level value rather than a prop, deliberately. The Statement of Advice builders sit five
 * components below the place that knows which firm is signed in, and the alternative is threading one
 * string through every one of them — including components that have no business knowing about firms
 * at all. "Which firm is signed in" is genuinely application-wide state; a prop chain would not make
 * it less so, only harder to follow.
 *
 * Null is the honest default and the safe one: a consumer working on their own file has no firm, and
 * a document with nothing here falls back to the placeholder it has always shown.
 */
let currentAttribution: Attribution | null = null;

/** Set when a firm's workspace resolves; cleared on sign-out. */
export function setCurrentAdviceAttribution(next: Attribution | null): void {
    currentAttribution = next;
}

export function getCurrentAdviceAttribution(): Attribution | null {
    return currentAttribution;
}
